import React from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Image,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faWeight,
  faCube,
  faTag,
  faBarcode,
  faImages,
  faDollarSign,
  faBox,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import ProductInfoCardStyle from '../../../assets/css/components/client/products/ProductInfoCardStyle';

const ProductInfoCard = ({ product, onClose, loading }) => {
  if (!product && !loading) return null;

  // Imagen placeholder en base64 para evitar errores de carga
  const placeholderImage = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjFmNWY5Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY0NzQ4YiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlbjwvdGV4dD48L3N2Zz4=";

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) {
      return 'No disponible';
    }
    return `$${price.toFixed(2)}`;
  };

  const formatWeight = (weight) => {
    if (!weight) return 'No disponible';
    return `${weight} kg`;
  };

  const formatVolume = (volume) => {
    if (!volume) return 'No disponible';
    return `${volume} m³`;
  };

  const renderDescription = (description) => {
    if (!description) return 'No disponible';
    
    // Limpiar HTML básico y mantener solo texto
    const cleanText = description
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Limitar a 200 caracteres
    if (cleanText.length > 200) {
      return cleanText.substring(0, 200) + '...';
    }
    
    return cleanText;
  };

  const handleImageError = (e) => {
    // Evitar bucle infinito verificando si ya estamos mostrando el placeholder
    if (e.target.src !== placeholderImage) {
      e.target.src = placeholderImage;
    }
  };

  return (
    <ProductInfoCardStyle.Overlay>
      <ProductInfoCardStyle.Container>
        <Card className="h-100 border-0 shadow-lg">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-primary fw-bold">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Información del Producto
            </h5>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={onClose}
              className="rounded-circle"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </Card.Header>

          <Card.Body className="p-4">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" size="lg" />
                <p className="mt-3 text-muted">Cargando información del producto...</p>
              </div>
            ) : product ? (
              <Row>
                {/* Imagen principal */}
                <Col md={4} className="mb-4">
                  <ProductInfoCardStyle.ImageContainer>
                    <Image
                      src={product.thumbnail?.url || placeholderImage}
                      alt={product.title}
                      fluid
                      rounded
                      className="main-product-image"
                      onError={handleImageError}
                    />
                    {product.attrib?.imagesPlus && product.attrib.imagesPlus.length > 0 && (
                      <Badge bg="info" className="images-count-badge">
                        <FontAwesomeIcon icon={faImages} className="me-1" />
                        +{product.attrib.imagesPlus.length}
                      </Badge>
                    )}
                  </ProductInfoCardStyle.ImageContainer>
                </Col>

                {/* Información principal */}
                <Col md={8}>
                  <ProductInfoCardStyle.ProductTitle>
                    {product.title}
                  </ProductInfoCardStyle.ProductTitle>

                  <Row className="mb-3">
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                          Precio de Venta
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue className={product.price > 0 ? 'text-success' : 'text-muted'}>
                          {formatPrice(product.price)}
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faBox} className="me-2 text-info" />
                          Stock Disponible
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue className={product.stock > 0 ? 'text-success' : 'text-danger'}>
                          {product.stock} unidades
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faWeight} className="me-2 text-warning" />
                          Peso
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue>
                          {formatWeight(product.attrib?.weight)}
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faCube} className="me-2 text-primary" />
                          Volumen
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue>
                          {formatVolume(product.attrib?.volume)}
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                  </Row>

                  {/* Códigos y referencias */}
                  <Row className="mb-3">
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faBarcode} className="me-2 text-secondary" />
                          Código de Barras
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue>
                          {product.attrib?.barCode || 'No disponible'}
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                    <Col sm={6}>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faTag} className="me-2 text-info" />
                          Referencia Interna
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.InfoValue>
                          {product.attrib?.ref_int || 'No disponible'}
                        </ProductInfoCardStyle.InfoValue>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                  </Row>

                  {/* Marca */}
                  {product.attrib?.x_studio_marca && (
                    <Row className="mb-3">
                      <Col>
                        <ProductInfoCardStyle.InfoItem>
                          <ProductInfoCardStyle.InfoLabel>
                            <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
                            Marca
                          </ProductInfoCardStyle.InfoLabel>
                          <Badge bg="primary" className="ms-2">
                            {product.attrib.x_studio_marca}
                          </Badge>
                        </ProductInfoCardStyle.InfoItem>
                      </Col>
                    </Row>
                  )}

                  {/* Descripción */}
                  <Row>
                    <Col>
                      <ProductInfoCardStyle.InfoItem>
                        <ProductInfoCardStyle.InfoLabel>
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                          Descripción
                        </ProductInfoCardStyle.InfoLabel>
                        <ProductInfoCardStyle.Description>
                          {renderDescription(product.attrib?.description)}
                        </ProductInfoCardStyle.Description>
                      </ProductInfoCardStyle.InfoItem>
                    </Col>
                  </Row>

                  {/* Imágenes adicionales */}
                  {product.attrib?.imagesPlus && product.attrib.imagesPlus.length > 0 && (
                    <Row className="mt-4">
                      <Col>
                        <h6 className="text-primary mb-3">
                          <FontAwesomeIcon icon={faImages} className="me-2" />
                          Imágenes Adicionales ({product.attrib.imagesPlus.length})
                        </h6>
                        <ProductInfoCardStyle.AdditionalImages>
                          {product.attrib.imagesPlus.slice(0, 4).map((img, index) => (
                            <ProductInfoCardStyle.AdditionalImage key={index}>
                              <Image
                                src={img.url}
                                alt={`${product.title} - imagen ${index + 1}`}
                                fluid
                                rounded
                                onError={handleImageError}
                              />
                            </ProductInfoCardStyle.AdditionalImage>
                          ))}
                          {product.attrib.imagesPlus.length > 4 && (
                            <ProductInfoCardStyle.MoreImages>
                              +{product.attrib.imagesPlus.length - 4} más
                            </ProductInfoCardStyle.MoreImages>
                          )}
                        </ProductInfoCardStyle.AdditionalImages>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            ) : (
              <div className="text-center py-5">
                <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-muted mb-3" />
                <p className="text-muted">No se pudo cargar la información del producto</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </ProductInfoCardStyle.Container>
    </ProductInfoCardStyle.Overlay>
  );
};

export default ProductInfoCard; 